import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import axios from '@src/service/axios';

export const getDashboardExecutiveOptions = createAsyncThunk('dashboardSlice/getDashboardExecutiveOptions', async (data) => {
    try {
        let url = '/dashboard/executive-options?skip=' + (data?.skip || 0) + '&take=' + (data?.take || 20);

        if (data?.search) {
            url += '&search=' + encodeURIComponent(data.search);
        }

        let response = await axios.get(url, {
            headers: {
                'Content-Type': 'application/json'
            }
        });

        return Promise.resolve(response?.data?.data);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const getLeadManagementDashboard = createAsyncThunk('dashboardSlice/getLeadManagementDashboard', async (data) => {
    try {
        let url = '/dashboard/lead-management?startDate=' + data.startDate + '&endDate=' + data.endDate;

        if (data?.leadOwnerId) {
            url += '&leadOwnerId=' + data.leadOwnerId;
        }

        let response = await axios.get(url, {
            headers: {
                'Content-Type': 'application/json'
            }
        });

        return Promise.resolve(response?.data?.data);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const getCallingFollowUpDashboard = createAsyncThunk('dashboardSlice/getCallingFollowUpDashboard', async (data) => {
    try {
        let url = '/dashboard/call-followup-dashboard?startDate=' + data.startDate + '&endDate=' + data.endDate;

        if (data?.leadOwnerId) {
            url += '&leadOwnerId=' + data.leadOwnerId;
        }

        let response = await axios.get(url, {
            headers: {
                'Content-Type': 'application/json'
            }
        });

        return Promise.resolve(response?.data?.data);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const getConversionSalesDashboard = createAsyncThunk('dashboardSlice/getConversionSalesDashboard', async (data) => {
    try {
        let url = '/dashboard/conversion-sales-dashboard?startDate=' + data.startDate + '&endDate=' + data.endDate;

        if (data?.leadOwnerId) {
            url += '&leadOwnerId=' + data.leadOwnerId;
        }

        let response = await axios.get(url, {
            headers: {
                'Content-Type': 'application/json'
            }
        });

        return Promise.resolve(response?.data?.data);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const getDashboardCampaignOptions = createAsyncThunk('dashboardSlice/getDashboardCampaignOptions', async (data) => {
    try {
        let url = '/dashboard/campaign-options?skip=' + (data?.skip || 0) + '&take=' + (data?.take || 20);

        if (data?.search) url += '&search=' + encodeURIComponent(data.search);

        let response = await axios.get(url, { headers: { 'Content-Type': 'application/json' } });
        return Promise.resolve(response?.data?.data);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const getMarketingCampaignDashboard = createAsyncThunk('dashboardSlice/getMarketingCampaignDashboard', async (data) => {
    try {
        let url = '/dashboard/marketing-campaign?startDate=' + data.startDate + '&endDate=' + data.endDate;

        if (data?.campaignId) url += '&campaignId=' + data.campaignId;

        let response = await axios.get(url, { headers: { 'Content-Type': 'application/json' } });
        return Promise.resolve(response?.data?.data);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

export const getExecutivePerformanceDashboard = createAsyncThunk('dashboardSlice/getExecutivePerformanceDashboard', async (data) => {
    try {
        let url = '/dashboard/executive-performance?startDate=' + data.startDate + '&endDate=' + data.endDate;

        if (data?.leadOwnerId) url += '&leadOwnerId=' + data.leadOwnerId;

        let response = await axios.get(url, { headers: { 'Content-Type': 'application/json' } });
        return Promise.resolve(response?.data?.data);
    } catch (err) {
        console.log('err', err);
        toast.error(err?.response?.data?.message || 'Something went wrong!');
        return Promise.reject(err);
    }
});

const initialState = {
    activeTab: 'lead-management',
    leadManagement: null,
    leadManagementLoading: false,
    callingFollowUp: [],
    callingFollowUpLoading: false,
    salesConversion: [],
    salesConversionLoading: false,
    marketingCampaign: null,
    marketingCampaignLoading: false,
    executivePerformance: null,
    executivePerformanceLoading: false
};

export const dashboardSlice = createSlice({
    name: 'dashboardSlice',
    initialState,
    reducers: {
        setActiveDashboardTab: (state, action) => {
            state.activeTab = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getLeadManagementDashboard.pending, (state) => {
            state.leadManagementLoading = true;
        });
        builder.addCase(getLeadManagementDashboard.fulfilled, (state, action) => {
            state.leadManagement = action.payload;
            state.leadManagementLoading = false;
        });
        builder.addCase(getLeadManagementDashboard.rejected, (state) => {
            state.leadManagementLoading = false;
        });
        builder.addCase(getCallingFollowUpDashboard.pending, (state) => {
            state.callingFollowUpLoading = true;
        });
        builder.addCase(getCallingFollowUpDashboard.fulfilled, (state, action) => {
            state.callingFollowUp = action.payload;
            state.callingFollowUpLoading = false;
        });
        builder.addCase(getCallingFollowUpDashboard.rejected, (state) => {
            state.callingFollowUpLoading = false;
        });
        builder.addCase(getConversionSalesDashboard.pending, (state) => {
            state.salesConversionLoading = true;
        });
        builder.addCase(getConversionSalesDashboard.fulfilled, (state, action) => {
            state.salesConversion = action.payload;
            state.salesConversionLoading = false;
        });
        builder.addCase(getConversionSalesDashboard.rejected, (state) => {
            state.salesConversionLoading = false;
        });
        builder.addCase(getMarketingCampaignDashboard.pending, (state) => {
            state.marketingCampaignLoading = true;
        });
        builder.addCase(getMarketingCampaignDashboard.fulfilled, (state, action) => {
            state.marketingCampaign = action.payload;
            state.marketingCampaignLoading = false;
        });
        builder.addCase(getMarketingCampaignDashboard.rejected, (state) => {
            state.marketingCampaignLoading = false;
        });
        builder.addCase(getExecutivePerformanceDashboard.pending, (state) => {
            state.executivePerformanceLoading = true;
        });
        builder.addCase(getExecutivePerformanceDashboard.fulfilled, (state, action) => {
            state.executivePerformance = action.payload;
            state.executivePerformanceLoading = false;
        });
        builder.addCase(getExecutivePerformanceDashboard.rejected, (state) => {
            state.executivePerformanceLoading = false;
        });
    }
});

export const { setActiveDashboardTab } = dashboardSlice.actions;

export default dashboardSlice.reducer;
